import { EntityMetric } from '@/lib/enums';
import isPercentageMetric from './isPercentageMetric';

/**
 * Change of a metric between the current design (row.data) and the comparison
 * design (row.comparison.data).
 *
 * Relative (default): (current - comparison) / comparison. Percentage metrics
 * compare share-of-total rather than raw values, matching the comparison shown
 * in the chart tooltip. Returns null when there is no comparison baseline (an
 * entity present only in the current design), since a relative change is
 * undefined without a prior value to divide by.
 *
 * Absolute: the un-divided delta current - comparison (a percentage-point
 * difference for percentage metrics). A missing baseline counts as zero, so the
 * result is always finite.
 *
 * @param { Object } row
 * @param { string } metric
 * @param { Object } [metricTotals]
 * @param { boolean } [absolute]
 *
 * @return { number | null }
 */
export default function getComparisonChange(
  row,
  metric,
  metricTotals,
  absolute = false
) {
  const { data, comparison, parentData } = row;
  const isEntityPercentage = metric === EntityMetric.ENTITY_PERCENTAGE;
  const isRelativePercentage = metric === EntityMetric.RELATIVE_PERCENTAGE;
  const percentageMetric = isPercentageMetric(metric);
  const baseMetric = percentageMetric ? EntityMetric.HOURS : metric;

  const metricValue = data?.[baseMetric] ?? 0;
  const comparisonValue = comparison?.data?.[baseMetric] || null;

  if (!percentageMetric) {
    if (absolute) {
      return metricValue - (comparisonValue ?? 0);
    }

    return comparisonValue
      ? (metricValue - comparisonValue) / comparisonValue
      : null;
  }

  const current =
    (isEntityPercentage || isRelativePercentage) && parentData
      ? (data?.[metric] ?? 0) / 100
      : metricTotals?.[baseMetric]
        ? metricValue / metricTotals[baseMetric]
        : 0;

  const comparisonShare =
    (isEntityPercentage || isRelativePercentage) &&
    parentData &&
    comparison?.data
      ? comparison.data[metric] / 100
      : metricTotals?.comparison?.[baseMetric]
        ? comparisonValue / metricTotals.comparison[baseMetric]
        : null;

  if (absolute) {
    return current - (comparisonShare ?? 0);
  }

  return comparisonShare ? (current - comparisonShare) / comparisonShare : null;
}
